/**
 * Drives a dashboard canvas: when each box is planned, saved, and run.
 *
 * The first submit plans every box in parallel, saves the charts that came back
 * as one draft dashboard, and runs them together. After that each box recovers
 * on its own (clarify, pick a chart, retry, edit) and is added to the same
 * dashboard. A box that fails never blocks the others.
 */
import { useRef, useState } from "react";
import type { Dashboard } from "../../types";
import {
  applyPlan,
  applyResult,
  canSubmit,
  clarifiedQuestion,
  createBoxes,
  describeError,
  draftForSlot,
  editing,
  failed,
  markSaved,
  plannerQuestion,
  planning,
  questionProblem,
  running,
  withKind,
  type BoxKind,
  type CanvasBox,
  type CanvasSource,
} from "./canvasModel";
import {
  attachPanels,
  createDashboard,
  discardPanel,
  logCanvas,
  planQuestion,
  runPanels,
} from "./canvasService";

export const DEFAULT_DASHBOARD_NAME = "Untitled dashboard";

export interface DashboardCanvas {
  boxes: CanvasBox[];
  /** The draft dashboard, once the first chart has been saved. */
  dashboard: Dashboard | null;
  /** True after the first submit; from then on boxes are asked one by one. */
  submitted: boolean;
  submitting: boolean;
  /** A canvas-wide problem, as opposed to one that belongs to a box. */
  notice: string | null;
  setQuestion: (boxId: string, question: string) => void;
  setKind: (boxId: string, kind: BoxKind) => void;
  submit: () => Promise<void>;
  ask: (box: CanvasBox) => Promise<void>;
  retry: (box: CanvasBox) => Promise<void>;
  edit: (box: CanvasBox) => void;
  chooseOption: (box: CanvasBox, option: string) => Promise<void>;
  keepCandidate: (box: CanvasBox, index: number) => Promise<void>;
  startOver: () => void;
}

export function useDashboardCanvas(
  source: CanvasSource | null,
  name: string,
): DashboardCanvas {
  const [boxes, setBoxes] = useState(createBoxes);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Held as a promise so boxes finishing together share one dashboard instead
  // of each creating their own.
  const dashboardRef = useRef<Promise<Dashboard> | null>(null);
  // Bumped by start over, so answers for the previous canvas are dropped.
  const generation = useRef(0);

  function put(next: CanvasBox, gen: number): void {
    if (gen !== generation.current) return;
    setBoxes((current) => current.map((box) => (box.id === next.id ? next : box)));
  }

  async function save(
    list: CanvasBox[],
    within: CanvasSource,
    gen: number,
  ): Promise<Dashboard> {
    const drafts = list.flatMap((box) => (box.panel ? [box.panel] : []));
    if (!dashboardRef.current) {
      const pending = createDashboard(name.trim() || DEFAULT_DASHBOARD_NAME, within, drafts);
      dashboardRef.current = pending;
      try {
        const created = await pending;
        if (gen === generation.current) setDashboard(created);
        return created;
      } catch (error) {
        if (dashboardRef.current === pending) dashboardRef.current = null;
        throw error;
      }
    }
    const existing = await dashboardRef.current;
    await attachPanels(existing.id, drafts);
    return existing;
  }

  /** Saves whichever boxes are not saved yet, then runs them all. Never throws. */
  async function saveAndRun(
    list: CanvasBox[],
    within: CanvasSource,
    gen: number,
  ): Promise<void> {
    list.forEach((box) => put(box, gen));
    const unsaved = list.filter((box) => !box.saved);
    let target: Dashboard;
    try {
      if (unsaved.length) {
        target = await save(unsaved, within, gen);
      } else if (dashboardRef.current) {
        target = await dashboardRef.current;
      } else {
        throw new Error("No dashboard to run the chart on.");
      }
    } catch (error) {
      const detail = describeError(error);
      list.forEach((box) => put(failed(box, detail), gen));
      return;
    }

    const saved = list.map(markSaved);
    saved.forEach((box) => put(box, gen));
    const ids = saved.flatMap((box) => (box.panel?.id ? [box.panel.id] : []));
    try {
      const results = await runPanels(target.id, ids);
      saved.forEach((box) => put(applyResult(box, results.get(box.panel?.id ?? "")), gen));
    } catch (error) {
      const detail = describeError(error);
      saved.forEach((box) => put(failed(box, detail), gen));
    }
  }

  /** Plans one box. Never throws; failures land in the returned box. */
  async function plan(
    box: CanvasBox,
    question: string,
    within: CanvasSource,
    gen: number,
  ): Promise<CanvasBox> {
    const start = planning(box, question);
    put(start, gen);
    let next: CanvasBox;
    try {
      next = applyPlan(start, await planQuestion(plannerQuestion(question, box.kind), within));
      if (next.phase === "running" && next.panel) {
        next = running(
          next,
          draftForSlot(next.panel, box.slot, within, crypto.randomUUID()),
        );
      }
    } catch (error) {
      next = failed(start, describeError(error));
    }
    put(next, gen);
    return next;
  }

  async function answer(box: CanvasBox, question: string): Promise<void> {
    if (!source) {
      setNotice("Choose a governed source first.");
      return;
    }
    const gen = generation.current;
    const next = await plan(box, question, source, gen);
    if (next.phase === "running") await saveAndRun([next], source, gen);
  }

  function setQuestion(boxId: string, question: string): void {
    setBoxes((current) =>
      current.map((box) =>
        box.id === boxId && box.phase === "draft" ? { ...box, question } : box,
      ),
    );
  }

  function setKind(boxId: string, kind: BoxKind): void {
    setBoxes((current) =>
      current.map((box) => (box.id === boxId ? withKind(box, kind) : box)),
    );
  }

  async function submit(): Promise<void> {
    if (!source) {
      setNotice("Choose a governed source first.");
      return;
    }
    if (submitted || !canSubmit(boxes)) return;
    const gen = generation.current;
    setSubmitted(true);
    setSubmitting(true);
    setNotice(null);
    logCanvas("info", "submitted", { boxes: boxes.length, contextId: source.contextId });
    try {
      const planned = await Promise.all(
        boxes.map((box) => plan(box, box.question.trim(), source, gen)),
      );
      const charts = planned.filter((box) => box.phase === "running");
      if (charts.length) await saveAndRun(charts, source, gen);
    } catch (error) {
      // plan and saveAndRun handle their own failures; reaching here is a bug.
      logCanvas("error", "submit_failed", {
        code: error instanceof Error ? error.name : "unknown",
      });
      if (gen === generation.current) {
        setNotice("Something went wrong while building the dashboard. Retry the boxes that did not finish.");
      }
    } finally {
      if (gen === generation.current) setSubmitting(false);
    }
  }

  async function ask(box: CanvasBox): Promise<void> {
    if (box.phase !== "draft" || questionProblem(box.question)) return;
    await answer(box, box.question.trim());
  }

  async function retry(box: CanvasBox): Promise<void> {
    if (box.panel && source) {
      await saveAndRun([running(box, box.panel, box.saved)], source, generation.current);
      return;
    }
    await answer(box, box.question.trim());
  }

  function edit(box: CanvasBox): void {
    if (box.saved && box.panel?.id) void discardPanel(box.panel.id);
    put(editing(box), generation.current);
  }

  async function chooseOption(box: CanvasBox, option: string): Promise<void> {
    await answer(box, clarifiedQuestion(box.question, option));
  }

  async function keepCandidate(box: CanvasBox, index: number): Promise<void> {
    const candidate = box.candidates[index];
    if (!candidate || !source) return;
    const draft = draftForSlot(candidate, box.slot, source, crypto.randomUUID());
    await saveAndRun([running(box, draft)], source, generation.current);
  }

  function startOver(): void {
    generation.current += 1;
    dashboardRef.current = null;
    setBoxes(createBoxes());
    setDashboard(null);
    setSubmitted(false);
    setSubmitting(false);
    setNotice(null);
    logCanvas("info", "started_over");
  }

  return {
    boxes,
    dashboard,
    submitted,
    submitting,
    notice,
    setQuestion,
    setKind,
    submit,
    ask,
    retry,
    edit,
    chooseOption,
    keepCandidate,
    startOver,
  };
}
